import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import type { GuideConfig, GuideArticle } from '@edison/help';
import { GuidesColumn } from './guides-column.js';
import { AIChatColumn } from './ai-chat-column.js';
import { MyTicketsColumn } from './my-tickets-column.js';

interface HelpCenterProps {
  novaProjectId: string;
  guides: GuideConfig;
  appContext?: string;
  theme?: 'dark' | 'system';
}

// ─── Guide renderer ────────────────────────────────────────────────────────

const mdComponents: Components = {
  h2: ({ children }) => (
    <h2 className="text-base font-semibold text-white mt-7 mb-3 pb-2 border-b border-white/10 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <div className="flex items-center gap-2 mt-5 mb-2">
      <span className="w-0.5 h-3.5 bg-blue-500 rounded-full shrink-0" aria-hidden="true" />
      <h3 className="text-sm font-semibold text-white/80">{children}</h3>
    </div>
  ),
  p: ({ children }) => (
    <p className="text-sm text-white/65 leading-relaxed mb-3 last:mb-0">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="space-y-1.5 mb-4 list-none pl-0">{children}</ul>
  ),
  ol: ({ children }) => {
    let i = 0;
    const numbered = React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;
      i++;
      return React.cloneElement(
        child as React.ReactElement<Record<string, unknown>>,
        { 'data-step': i },
      );
    });
    return <ol className="space-y-2 mb-4 list-none pl-0">{numbered}</ol>;
  },
  li: (props) => {
    const step = (props as Record<string, unknown>)['data-step'] as number | undefined;
    if (step !== undefined) {
      return (
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-600/25 border border-blue-500/35 flex items-center justify-center text-[10px] font-bold text-blue-300 mt-0.5 tabular-nums">
            {step}
          </span>
          <div className="text-sm text-white/70 leading-relaxed flex-1">{props.children}</div>
        </li>
      );
    }
    return (
      <li className="flex items-start gap-2.5">
        <span className="w-1 h-1 rounded-full bg-blue-400/50 mt-[7px] shrink-0" aria-hidden="true" />
        <span className="text-sm text-white/65 leading-relaxed flex-1">{props.children}</span>
      </li>
    );
  },
  blockquote: ({ children }) => (
    <div className="border-l-2 border-blue-500/50 bg-blue-500/5 rounded-r-lg px-4 py-3 my-4">
      <div className="text-sm text-blue-200/75 leading-relaxed [&>p]:mb-0 [&>p]:text-blue-200/75">
        {children}
      </div>
    </div>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto mb-4 rounded-lg border border-white/10">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-white/5 border-b border-white/10">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-white/40">
      {children}
    </th>
  ),
  tr: ({ children }) => (
    <tr className="border-b border-white/5 last:border-0 even:bg-white/[0.02]">{children}</tr>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2.5 text-white/60 text-xs align-top">{children}</td>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-white">{children}</strong>
  ),
  pre: ({ children }) => (
    <pre className="bg-white/5 rounded-md p-3 overflow-x-auto mb-3 border border-white/8">
      {children}
    </pre>
  ),
  code: ({ children, className }) => {
    if (className) {
      return <code className="text-xs text-white/70 font-mono">{children}</code>;
    }
    return (
      <code className="bg-white/8 text-white/85 px-1.5 py-0.5 rounded text-[11px] font-mono">
        {children}
      </code>
    );
  },
  hr: () => <hr className="border-white/8 my-6" />,
};

function GuideRenderer({ content }: { content: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
      {content}
    </ReactMarkdown>
  );
}

// ─── HelpCenter ────────────────────────────────────────────────────────────

export function HelpCenter({ guides, appContext }: HelpCenterProps) {
  const [citedGuideId, setCitedGuideId] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<GuideArticle | null>(null);
  const context = appContext ?? guides.appContext;

  function handleArticleSelect(article: GuideArticle) {
    setSelectedArticle((prev) => prev?.id === article.id ? null : article);
  }

  function handleMessageSent() {
    setSelectedArticle(null);
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[oklch(0.22_0.05_250)]">
      {/* Page header */}
      <div className="px-6 py-4 border-b border-white/8 shrink-0">
        <h1 className="text-2xl font-semibold text-primary-foreground">Help &amp; Support</h1>
      </div>

      {/* Three-column body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Guides (220px, hidden on sm) */}
        <div className="hidden sm:flex flex-col w-[220px] shrink-0 border-r border-white/8">
          <div className="px-4 py-3 border-b border-white/8 shrink-0">
            <span className="text-xs font-semibold uppercase tracking-widest text-white/40">
              Guides
            </span>
          </div>
          <GuidesColumn
            config={guides}
            citedGuideId={citedGuideId}
            selectedArticleId={selectedArticle?.id ?? null}
            onArticleSelect={handleArticleSelect}
            onClearCitation={() => setCitedGuideId(null)}
          />
        </div>

        {/* Centre */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {selectedArticle ? (
            <>
              {/* Guide reader header */}
              <div className="px-4 py-3 border-b border-white/8 shrink-0 flex items-center gap-3">
                <button
                  onClick={() => setSelectedArticle(null)}
                  className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors"
                  aria-label="Back to chat"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </button>
                <span className="text-xs font-semibold uppercase tracking-widest text-white/40 flex-1 truncate">
                  {selectedArticle.title}
                </span>
              </div>

              {/* Article content */}
              <div className="flex-1 overflow-y-auto">
                {/* Title band */}
                <div className="px-8 pt-7 pb-5 border-b border-white/6">
                  <h2 className="text-xl font-semibold text-white leading-snug">
                    {selectedArticle.title}
                  </h2>
                </div>
                {/* Body */}
                <div className="px-8 py-6 max-w-2xl">
                  <GuideRenderer content={selectedArticle.content} />
                </div>
              </div>

              {/* Chat input — always visible at bottom */}
              <div className="border-t border-white/8 shrink-0">
                <AIChatColumn
                  appContext={context}
                  onGuideCited={(id) => setCitedGuideId(id)}
                  onMessageSent={handleMessageSent}
                  compact
                />
              </div>
            </>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-white/8 shrink-0">
                <span className="text-xs font-semibold uppercase tracking-widest text-white/40">
                  Edison AI
                </span>
              </div>
              <AIChatColumn
                appContext={context}
                onGuideCited={(id) => setCitedGuideId(id)}
                onMessageSent={handleMessageSent}
              />
            </>
          )}
        </div>

        {/* Right: My Tickets (200px, hidden below md) */}
        <div className="hidden md:flex flex-col w-[200px] shrink-0 border-l border-white/8">
          <div className="px-4 py-3 border-b border-white/8 shrink-0">
            <span className="text-xs font-semibold uppercase tracking-widest text-white/40">
              My Tickets
            </span>
          </div>
          <MyTicketsColumn />
        </div>
      </div>
    </div>
  );
}
